import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { validateEmail, validatePassword, validateName, logSecurityEvent } from '@/lib/security';
import { useToast } from '@/hooks/use-toast';

export const useSecureAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const secureSignUp = useCallback(async (email: string, password: string, firstName?: string) => {
    setIsLoading(true);
    
    try {
      // Validate inputs
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        toast({
          title: "Invalid Email",
          description: emailValidation.error,
          variant: "destructive"
        });
        return { error: { message: emailValidation.error } };
      }
      
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        toast({
          title: "Invalid Password",
          description: passwordValidation.error,
          variant: "destructive"
        });
        return { error: { message: passwordValidation.error } };
      }
      
      if (firstName) {
        const nameValidation = validateName(firstName);
        if (!nameValidation.isValid) {
          toast({
            title: "Invalid Name",
            description: nameValidation.error,
            variant: "destructive"
          });
          return { error: { message: nameValidation.error } };
        }
      }
      
      // Log security event
      logSecurityEvent('signup_attempt', { email: email.substring(0, 3) + '***' });
      
      // Proceed with signup with proper email redirect
      const redirectUrl = `${window.location.origin}/`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName
          }
        }
      });
      
      if (error) {
        logSecurityEvent('signup_failed', { 
          error: error.message,
          email: email.substring(0, 3) + '***'
        });
        
        toast({
          title: "Sign Up Failed",
          description: error.message,
          variant: "destructive"
        });
        return { error };
      }
      
      logSecurityEvent('signup_success', { 
        userId: data.user?.id,
        email: email.substring(0, 3) + '***'
      });
      
      toast({
        title: "Account Created",
        description: "Please check your email to verify your account."
      });
      
      return { data, error: null };
      
    } catch (error: any) {
      logSecurityEvent('signup_error', { 
        error: error.message,
        email: email.substring(0, 3) + '***'
      });
      
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      
      return { error: { message: "An unexpected error occurred" } };
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const secureSignIn = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    
    try {
      // Validate inputs
      const emailValidation = validateEmail(email);
      if (!emailValidation.isValid) {
        toast({
          title: "Invalid Email",
          description: emailValidation.error,
          variant: "destructive"
        });
        return { error: { message: emailValidation.error } };
      }
      
      if (!password || password.length === 0) {
        toast({
          title: "Invalid Password",
          description: "Password is required",
          variant: "destructive"
        });
        return { error: { message: "Password is required" } };
      }
      
      // Log security event
      logSecurityEvent('signin_attempt', { email: email.substring(0, 3) + '***' });
      
      // Proceed with signin
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        logSecurityEvent('signin_failed', { 
          error: error.message,
          email: email.substring(0, 3) + '***'
        });
        
        toast({
          title: "Sign In Failed",
          description: error.message,
          variant: "destructive"
        });
        return { error };
      }
      
      logSecurityEvent('signin_success', { 
        userId: data.user?.id,
        email: email.substring(0, 3) + '***'
      });
      
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in."
      });
      
      return { data, error: null };
      
    } catch (error: any) {
      logSecurityEvent('signin_error', { 
        error: error.message,
        email: email.substring(0, 3) + '***'
      });
      
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      
      return { error: { message: "An unexpected error occurred" } };
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const secureSignOut = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logSecurityEvent('signout_failed', { error: error.message });
        toast({
          title: "Sign Out Failed",
          description: error.message,
          variant: "destructive"
        });
        return { error };
      }
      
      logSecurityEvent('signout_success', {});
      
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out."
      });
      
      return { error: null };
      
    } catch (error: any) {
      logSecurityEvent('signout_error', { error: error.message });
      
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred during sign out.",
        variant: "destructive"
      });
      
      return { error: { message: "An unexpected error occurred" } };
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const secureGoogleSignIn = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // Log security event
      logSecurityEvent('google_signin_attempt', {});
      
      // Proceed with Google OAuth
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
        }
      });
      
      if (error) {
        logSecurityEvent('google_signin_failed', { error: error.message });
        
        toast({
          title: "Google Sign In Failed",
          description: error.message,
          variant: "destructive"
        });
        return { error };
      }
      
      logSecurityEvent('google_signin_initiated', {});
      
      // OAuth flow initiated - user will be redirected to Google
      return { data, error: null };
      
    } catch (error: any) {
      logSecurityEvent('google_signin_error', { error: error.message });
      
      toast({
        title: "Unexpected Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      
      return { error: { message: "An unexpected error occurred" } };
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  return {
    secureSignUp,
    secureSignIn,
    secureSignOut,
    secureGoogleSignIn,
    isLoading
  };
};